import React, { useEffect, useState, useCallback } from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { RichTextEditor } from '../../components/RichTextEditor';
import { toast, Toaster } from 'sonner';
import { Search, Plus, X, Upload, LogOut, Loader2, Trash2, Download, ExternalLink, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import OrderBuilder from './OrderBuilder';
import BulkProductEditor from './BulkProductEditor';
import { hasPct, recalcPricing } from '../../lib/pricing';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fad_admin_token';
const imgSrc = (u) => (u && u.startsWith('/api/') ? `${API}${u}` : u);

const Field = ({ label, children }) => (
  <label className="block mb-4">
    <span className="block text-[11px] font-display tracking-[0.18em] text-neutral-500 mb-1.5">{label}</span>
    {children}
  </label>
);

const MarginRow = ({ price, cost }) => {
  const p = Number(price); const c = Number(cost);
  if (!price && price !== 0) return null;
  if (cost === '' || cost == null || Number.isNaN(c)) {
    return <div className="text-[11px] text-neutral-400 mt-1" data-testid="margin-readout">Enter cost to see profit &amp; margin</div>;
  }
  const profit = p - c;
  const margin = p > 0 ? (profit / p) * 100 : 0;
  return (
    <div className="flex gap-4 text-[11px] mt-1" data-testid="margin-readout">
      <span className="text-neutral-500">Profit <strong className="text-neutral-800">${profit.toFixed(2)}</strong></span>
      <span className="text-neutral-500">Margin <strong className={margin < 0 ? 'text-red-600' : 'text-emerald-600'}>{margin.toFixed(1)}%</strong></span>
    </div>
  );
};

const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ---- Percentage-driven pricing helpers live in ../../lib/pricing ----

// Payment badge for the Admin Orders view — e.g. "Paid by card · CA$541.15 · Helcim 12345"
const PaymentBadge = ({ order, compact = false }) => {
  const isCard = order.payment_flow === 'helcim_card';
  const label = isCard ? 'Paid by card' : (order.payment_method_title || 'Wire transfer');
  const parts = [label];
  if (isCard && order.amount_charged_cad != null) parts.push(`CA$${fmtMoney(order.amount_charged_cad)}`);
  if (isCard && order.helcim_transaction_id) parts.push(`Helcim ${order.helcim_transaction_id}`);
  return (
    <span
      data-testid={`order-payment-badge-${order.id}`}
      title={parts.join(' · ')}
      className={`inline-flex items-center gap-1.5 font-display tracking-[0.1em] ${compact ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-1'} ${
        isCard ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
      }`}
    >
      {parts.join(' · ')}
    </span>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [orders, setOrders] = useState([]);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [statusChange, setStatusChange] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [ordernum, setOrdernum] = useState({ prefix: 'FAD-', suffix: '', start: 1001, padding: 4, next_preview: '', last_used: 0 });
  const [ordernumSaving, setOrdernumSaving] = useState(false);
  const [payReqSending, setPayReqSending] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [prodToDelete, setProdToDelete] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkMarkup, setBulkMarkup] = useState('');
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  // Website importer
  const [importUrl, setImportUrl] = useState('');
  const [importMode, setImportMode] = useState('auto');
  const [importMax, setImportMax] = useState(40);
  const [importMarkup, setImportMarkup] = useState('');
  const [importLeaveBlank, setImportLeaveBlank] = useState(false);
  const [importJob, setImportJob] = useState(null);
  const [importScanning, setImportScanning] = useState(false);
  const [importSelected, setImportSelected] = useState([]);
  const [importCommitting, setImportCommitting] = useState(false);
  const [fxRate, setFxRate] = useState('');
  const [fxSaving, setFxSaving] = useState(false);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Editable homepage hero + abandoned checkouts
  const [hero, setHero] = useState({ headline: { kicker: '', title: '', subtext: '' }, slides: [] });
  const [heroSaving, setHeroSaving] = useState(false);
  const [abandoned, setAbandoned] = useState([]);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [featuredCap, setFeaturedCap] = useState(12);
  const [featuredSaving, setFeaturedSaving] = useState(false);
  const [featuredDrag, setFeaturedDrag] = useState(null);

  const authFetch = useCallback((path, opts = {}) =>
    fetch(`${API}${path}`, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    const [p, c, o] = await Promise.all([
      authFetch('/api/admin/products'), authFetch('/api/admin/collections'), authFetch('/api/admin/orders'),
    ]);
    if (p.status === 401) { setToken(''); localStorage.removeItem(TOKEN_KEY); return; }
    setProducts(await p.json());
    setCollections(await c.json());
    setOrders(await o.json());
  }, [authFetch]);

  const ORDER_PIPELINE = [
    ['submitted', 'Submitted', false],
    ['awaiting_payment', 'Confirmed – Awaiting Payment', true],
    ['payment_received', 'Payment Received', true],
    ['in_production', 'In Production', true],
    ['quality_check', 'Ready to Ship / Quality Check', false],
    ['shipped', 'Shipped', true],
    ['completed', 'Completed', true],
    ['cancelled', 'Cancelled', true],
    ['on_hold', 'On Hold – Needs Info', true],
  ];
  const STATUS_ALIAS = { new: 'submitted', paid: 'payment_received', complete: 'completed' };
  const canonStatus = (s) => STATUS_ALIAS[s] || s || 'submitted';
  const STATUS_LABEL = Object.fromEntries(ORDER_PIPELINE.map(([k, l]) => [k, l]));
  const STATUS_EMAILS = new Set(ORDER_PIPELINE.filter(([, , e]) => e).map(([k]) => k));
  const fmtStatus = (s) => STATUS_LABEL[canonStatus(s)] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  const openStatusChange = (order, stage) => {
    const canon = canonStatus(stage);
    setStatusChange({
      order, stage: canon,
      notify: STATUS_EMAILS.has(canon),
      note: '',
      timeframe: order.estimated_timeframe || '',
      carrier: order.carrier || '',
      tracking_number: order.tracking_number || '',
      tracking_url: order.tracking_url || '',
    });
  };
  const confirmStatusChange = async () => {
    const sc = statusChange;
    if (!sc) return;
    const payload = { status: sc.stage, notify: sc.notify, note: sc.note };
    if (sc.stage === 'awaiting_payment' || sc.stage === 'in_production') payload.estimated_timeframe = sc.timeframe;
    if (sc.stage === 'shipped') { payload.carrier = sc.carrier; payload.tracking_number = sc.tracking_number; payload.tracking_url = sc.tracking_url; }
    const r = await authFetch(`/api/admin/orders/${sc.order.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (r.ok) {
      const u = await r.json();
      setOrders((prev) => prev.map((x) => (x.id === u.id ? u : x)));
      setViewingOrder(u);
      if (sc.notify && STATUS_EMAILS.has(sc.stage)) {
        if (u._email && u._email.ok) toast.success('Status updated · customer emailed');
        else if (u._email && u._email.reason === 'no_email') toast.error('Status saved, but no customer email on file — nothing sent');
        else toast.success('Status updated · (already emailed for this stage — not re-sent)');
      } else {
        toast.success('Status updated quietly (no email)');
      }
      setStatusChange(null);
    } else toast.error('Update failed');
  };
  const updateOrder = async (oid, patch) => {
    const r = await authFetch(`/api/admin/orders/${oid}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
    if (r.ok) { const u = await r.json(); setOrders((prev) => prev.map((x) => (x.id === oid ? u : x))); setViewingOrder(u); toast.success('Order updated'); }
    else toast.error('Update failed');
  };
  const resendOrderEmail = async (oid) => {
    const r = await authFetch(`/api/admin/orders/${oid}/resend-status`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
    toast[r.ok ? 'success' : 'error'](r.ok ? 'Status email resent to customer' : 'Resend failed (no customer email, or this stage sends no email)');
  };
  const toggleOrderSelect = (oid) => setSelectedOrders((prev) => (prev.includes(oid) ? prev.filter((x) => x !== oid) : [...prev, oid]));
  const toggleSelectAll = () => setSelectedOrders((prev) => (prev.length === orders.length ? [] : orders.map((o) => o.id)));
  const confirmDeleteOrders = async () => {
    const ids = deleteTarget || [];
    let ok = false;
    if (ids.length === 1) {
      const r = await authFetch(`/api/admin/orders/${ids[0]}`, { method: 'DELETE' });
      ok = r.ok;
    } else {
      const r = await authFetch('/api/admin/orders/bulk-delete', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }) });
      ok = r.ok;
    }
    if (ok) {
      setOrders((prev) => prev.filter((x) => !ids.includes(x.id)));
      setSelectedOrders((prev) => prev.filter((x) => !ids.includes(x)));
      if (viewingOrder && ids.includes(viewingOrder.id)) setViewingOrder(null);
      toast.success(ids.length > 1 ? `${ids.length} orders deleted` : 'Order deleted');
    } else toast.error('Delete failed');
    setDeleteTarget(null);
  };

  useEffect(() => { if (token) load(); }, [token, load]);
  useEffect(() => {
    if (tab !== 'settings' || !token) return;
    authFetch('/api/admin/settings/fx').then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setFxRate(String(d.usd_to_cad)); });
    authFetch('/api/admin/settings/order-number').then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setOrdernum(d); });
  }, [tab, token]);
  useEffect(() => {
    if (tab !== 'hero' || !token) return;
    authFetch('/api/admin/hero').then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (d) setHero({ headline: d.headline || { kicker: '', title: '', subtext: '' }, slides: d.slides || [] });
    });
  }, [tab, token]);
  useEffect(() => {
    if (tab !== 'abandoned' || !token) return;
    authFetch('/api/admin/abandoned-checkouts').then((r) => (r.ok ? r.json() : [])).then((d) => setAbandoned(Array.isArray(d) ? d : []));
  }, [tab, token]);
  useEffect(() => {
    if (tab !== 'featured' || !token) return;
    authFetch('/api/admin/featured').then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (d) { setFeaturedItems(d.products || []); setFeaturedCap(d.cap || 12); }
    });
  }, [tab, token]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Login failed');
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success('Welcome back');
    } catch (err) { toast.error(err.message); }
    finally { setLoggingIn(false); }
  };

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(''); };

  const saveProduct = async () => {
    setSaving(true);
    try {
      const isNew = editing._isNew;
      const body = { ...editing }; delete body._isNew;
      if (body.dimensions) {
        const d = body.dimensions;
        const empty = !d.length && !d.width && !d.height;
        body.dimensions = empty ? null : { length: d.length === '' ? null : Number(d.length), width: d.width === '' ? null : Number(d.width), height: d.height === '' ? null : Number(d.height), unit: d.unit || 'cm' };
      }
      const res = await authFetch(isNew ? '/api/admin/products' : `/api/admin/products/${editing.id}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Saved — live on the site on next page load');
      setEditing(null);
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const togglePublish = async (p) => {
    const next = p.published === false;
    await authFetch(`/api/admin/products/${p.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ published: next }),
    });
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, published: next } : x)));
    toast.success(next ? 'Product visible' : 'Product hidden');
  };

  const openPreview = (p) => window.open(`${window.location.origin}/product/${p.slug || p.id}?preview=1`, '_blank');
  const toggleProductSelect = (id) => setSelectedProducts((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const bulkAction = async (action) => {
    if (selectedProducts.length === 0) { toast.error('Select products first'); return; }
    const body = { ids: selectedProducts, action };
    if (action === 'markup') {
      if (bulkMarkup === '' || Number.isNaN(Number(bulkMarkup))) { toast.error('Enter a markup %'); return; }
      body.markup_pct = Number(bulkMarkup);
    }
    const r = await authFetch('/api/admin/products/bulk', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    if (!r.ok) { toast.error('Bulk action failed'); return; }
    const { affected } = await r.json();
    toast.success(`${affected} product(s) updated`);
    if (action === 'delete') setSelectedProducts([]);
    setBulkDeleteOpen(false); setBulkMarkup('');
    await load();
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await authFetch('/api/admin/upload', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      setEditing((e) => ({ ...e, images: [...(e.images || []), url] }));
      toast.success('Image uploaded');
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const uploadCollectionImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await authFetch('/api/admin/upload', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      setEditing((e) => ({ ...e, image: url }));
      toast.success('Image uploaded');
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const setVariant = (i, patch) => setEditing((e) => {
    let variants = e.variants.map((v, j) => (j === i ? { ...v, ...patch } : v));
    // Manually typing a variant price detaches it from the % rule.
    if ('price' in patch && !('upliftPct' in patch)) {
      variants = variants.map((v, j) => (j === i ? { ...v, upliftPct: '' } : v));
    }
    // Manually typing a variant cost detaches it from the cost % rule.
    if ('cost' in patch) {
      variants = variants.map((v, j) => (j === i ? { ...v, costManual: true } : v));
    }
    return recalcPricing({ ...e, variants });
  });
  const addVariant = () => setEditing((e) => recalcPricing({ ...e, variants: [...(e.variants || []), { option: '', price: e.price || 0, compareAtPrice: null, sku: '', upliftPct: '' }] }));
  const removeVariant = (i) => setEditing((e) => recalcPricing({ ...e, variants: (e.variants || []).filter((_, j) => j !== i) }));
  // Changing/setting the cost % re-attaches (re-derives) all cost fields.
  const setCostPct = (val) => setEditing((e) => recalcPricing({
    ...e, costPct: val, costManual: false,
    variants: e.variants ? e.variants.map((v) => ({ ...v, costManual: false })) : e.variants,
  }));

  const deleteProduct = async (pid) => {
    const r = await authFetch(`/api/admin/products/${pid}`, { method: 'DELETE' });
    if (r.ok) { setProducts((prev) => prev.filter((x) => x.id !== pid)); setEditing(null); toast.success('Product deleted'); }
    else toast.error('Delete failed');
    setDeleteTarget(null);
  };

  // ---- Website importer ----
  const startImportScan = async () => {
    if (!importUrl.trim().startsWith('http')) { toast.error('Enter a valid http(s) URL'); return; }
    setImportScanning(true); setImportJob(null); setImportSelected([]);
    try {
      const r = await authFetch('/api/admin/import/scan', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: importUrl.trim(), mode: importMode, max_products: Number(importMax) || 40 }),
      });
      if (!r.ok) throw new Error('Scan failed to start');
      const { job_id } = await r.json();
      pollImportJob(job_id);
    } catch (err) { toast.error(err.message); setImportScanning(false); }
  };

  const pollImportJob = async (jobId) => {
    for (let i = 0; i < 90; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 3000));
      // eslint-disable-next-line no-await-in-loop
      const r = await authFetch(`/api/admin/import/jobs/${jobId}`);
      if (!r.ok) continue;
      // eslint-disable-next-line no-await-in-loop
      const job = await r.json();
      setImportJob(job);
      if (job.status === 'done') {
        setImportSelected((job.candidates || []).map((_, idx) => idx));
        setImportScanning(false);
        toast.success(`Found ${(job.candidates || []).length} product(s)`);
        return;
      }
      if (job.status === 'error') { setImportScanning(false); toast.error('Scan failed: ' + (job.error || 'unknown')); return; }
    }
    setImportScanning(false);
    toast.error('Scan timed out');
  };

  const toggleImportSelect = (idx) => setImportSelected((prev) => (prev.includes(idx) ? prev.filter((x) => x !== idx) : [...prev, idx]));

  const commitImport = async () => {
    if (importSelected.length === 0) { toast.error('Select at least one product'); return; }
    setImportCommitting(true);
    try {
      const r = await authFetch('/api/admin/import/commit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: importJob.id, indexes: importSelected,
          markup_pct: importMarkup === '' ? null : Number(importMarkup),
          leave_price_blank: importLeaveBlank,
        }),
      });
      if (!r.ok) throw new Error('Import failed');
      const { created } = await r.json();
      toast.success(`${created} product(s) imported as drafts`);
      setImportJob(null); setImportUrl(''); setImportSelected([]);
      await load();
      setTab('products');
    } catch (err) { toast.error(err.message); }
    finally { setImportCommitting(false); }
  };

  const orderNo = (o) => o.order_number || `FAD-${o.fortune_order_number || o.id}`;
  const onOrderSaved = (order) => {
    setOrders((prev) => {
      const exists = prev.some((x) => x.id === order.id);
      return exists ? prev.map((x) => (x.id === order.id ? order : x)) : [order, ...prev];
    });
    if (viewingOrder && viewingOrder.id === order.id) setViewingOrder(order);
    setEditingOrder(null);
  };
  const sendPaymentRequest = async (o) => {
    if (!window.confirm(`Send a secure "Pay securely" email to ${o.customer_email || 'this customer'} for ${orderNo(o)}?\n\nThe order will move to Confirmed – Awaiting Payment and the total will be locked.`)) return;
    setPayReqSending(true);
    try {
      const r = await authFetch(`/api/admin/orders/${o.id}/send-payment-request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin: window.location.origin }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed to send payment request');
      const data = await r.json();
      const fresh = await authFetch(`/api/admin/orders`).then((res) => res.json());
      const updated = fresh.find((x) => x.id === o.id);
      if (updated) { setOrders(fresh); setViewingOrder(updated); }
      toast.success(`Payment request sent · CA$${Number(data.amount_cad).toLocaleString(undefined, { minimumFractionDigits: 2 })} locked`);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setPayReqSending(false); }
  };
  const saveOrdernum = async () => {
    setOrdernumSaving(true);
    try {
      const r = await authFetch('/api/admin/settings/order-number', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prefix: ordernum.prefix, suffix: ordernum.suffix, start: Number(ordernum.start), padding: Number(ordernum.padding) }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Save failed');
      const fresh = await authFetch('/api/admin/settings/order-number').then((res) => res.json());
      setOrdernum(fresh);
      toast.success('Order-number settings saved');
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setOrdernumSaving(false); }
  };

  const saveFxRate = async () => {
    const rate = Number(fxRate);
    if (Number.isNaN(rate) || rate < 0.5 || rate > 3.0) { toast.error('Enter a rate between 0.5 and 3.0'); return; }
    setFxSaving(true);
    try {
      const r = await authFetch('/api/admin/settings/fx', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ usd_to_cad: rate }) });
      if (!r.ok) throw new Error('Save failed');
      toast.success('Exchange rate saved');
    } catch (e) { toast.error(e.message); }
    finally { setFxSaving(false); }
  };

  // ---- Hero editor helpers ----
  const setHeadlineField = (k, v) => setHero((h) => ({ ...h, headline: { ...h.headline, [k]: v } }));
  const setSlideField = (idx, k, v) => setHero((h) => ({ ...h, slides: h.slides.map((s, i) => (i === idx ? { ...s, [k]: v } : s)) }));
  const addSlide = () => setHero((h) => ({ ...h, slides: [...h.slides, { image: '', label: '', ctaLabel: '', ctaTo: '' }] }));
  const removeSlide = (idx) => setHero((h) => ({ ...h, slides: h.slides.filter((_, i) => i !== idx) }));
  const moveSlide = (idx, dir) => setHero((h) => {
    const arr = [...h.slides];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return h;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...h, slides: arr };
  });
  const uploadHeroImage = async (idx, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await authFetch('/api/admin/upload', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      setSlideField(idx, 'image', url);
      toast.success('Image uploaded');
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };
  const saveHero = async () => {
    setHeroSaving(true);
    try {
      const r = await authFetch('/api/admin/hero', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(hero) });
      if (!r.ok) throw new Error('Save failed');
      toast.success('Homepage hero saved');
    } catch (e) { toast.error(e.message); }
    finally { setHeroSaving(false); }
  };

  // ---- Featured products (homepage carousel) ----
  const moveFeatured = (from, to) => {
    if (to < 0 || to >= featuredItems.length) return;
    setFeaturedItems((prev) => { const a = [...prev]; const [x] = a.splice(from, 1); a.splice(to, 0, x); return a; });
  };
  const removeFeatured = (id) => setFeaturedItems((prev) => prev.filter((p) => p.id !== id));
  const saveFeatured = async () => {
    setFeaturedSaving(true);
    try {
      const order = featuredItems.map((p) => p.id);
      const r = await authFetch('/api/admin/featured', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ order }) });
      if (!r.ok) throw new Error('Save failed');
      toast.success(`Featured set saved — ${order.length} on the homepage`);
      await load();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setFeaturedSaving(false); }
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
        <Toaster theme="dark" position="top-center" />
        <form onSubmit={doLogin} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="font-display tracking-[0.3em] text-[11px] text-[#FF6B00] font-semibold mb-2">FORTUNE AUTO DESIGN</div>
          <h1 className="font-display text-3xl text-white tracking-tight mb-8">Admin Login</h1>
          <Field label="EMAIL">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="h-11 rounded-none bg-transparent border-white/20 text-white focus-visible:border-[#FF6B00] focus-visible:ring-0"
              data-testid="admin-email" />
          </Field>
          <Field label="PASSWORD">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="h-11 rounded-none bg-transparent border-white/20 text-white focus-visible:border-[#FF6B00] focus-visible:ring-0"
              data-testid="admin-password" />
          </Field>
          <Button type="submit" disabled={loggingIn}
            className="w-full h-11 rounded-none bg-[#FF6B00] hover:bg-[#ff8124] text-white font-display tracking-[0.18em] mt-2"
            data-testid="admin-login-submit">
            {loggingIn ? <Loader2 className="animate-spin" size={16} /> : 'SIGN IN'}
          </Button>
        </form>
      </div>
    );
  }

  const filtered = products.filter((p) => !q || (p.name || '').toLowerCase().includes(q.toLowerCase()));

  const CSV_HEADERS = ['Handle', 'Title', 'Brand', 'Category', 'Collection', 'Description (HTML)', 'Price', 'Cost', 'Stock status', 'Published', 'SEO Title', 'SEO Description', 'Image URLs', 'Variant Option', 'Variant SKU', 'Variant Price'];
  const csvCell = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const productsToCsv = (list) => {
    const lines = [CSV_HEADERS.join(',')];
    list.forEach((p) => {
      const base = {
        Handle: p.slug || p.id, Title: p.name || '', Brand: p.brand || '', Category: p.category || '',
        Collection: p.collectionId || '', 'Description (HTML)': p.descriptionHtml || '',
        Price: p.price ?? '', Cost: p.cost ?? '', 'Stock status': p.stockStatus || '',
        Published: p.published === false ? 'FALSE' : 'TRUE', 'SEO Title': p.seoTitle || '', 'SEO Description': p.seoDescription || '',
        'Image URLs': (p.images || []).join(', '),
      };
      const variants = p.variants || [];
      const v0 = variants[0] || {};
      const row0 = { ...base, 'Variant Option': v0.option || '', 'Variant SKU': v0.sku || '', 'Variant Price': v0.price ?? '' };
      lines.push(CSV_HEADERS.map((h) => csvCell(row0[h])).join(','));
      variants.slice(1).forEach((v) => {
        const r = { Handle: base.Handle, 'Variant Option': v.option || '', 'Variant SKU': v.sku || '', 'Variant Price': v.price ?? '' };
        lines.push(CSV_HEADERS.map((h) => csvCell(r[h] != null ? r[h] : '')).join(','));
      });
    });
    return lines.join('\n');
  };
  const exportCsv = (scope) => {
    const list = scope === 'selected' ? products.filter((p) => selectedProducts.includes(p.id)) : filtered;
    if (!list.length) { toast.error('Nothing to export'); return; }
    const text = productsToCsv(list);
    const blob = new Blob(['\ufeff' + text], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `fad-products-${scope}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
    toast.success(`Exported ${list.length} product${list.length !== 1 ? 's' : ''}`);
  };
  const runImportPreview = async (file) => {
    if (!file) return;
    setImporting(true); setImportPreview(null); setImportResult(null); setImportOpen(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await authFetch('/api/admin/products/import/preview', { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Could not read that file.');
      setImportPreview(await r.json());
    } catch (e) { toast.error(String(e.message || e)); setImportOpen(false); }
    finally { setImporting(false); }
  };
  const runImportCommit = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const r = await authFetch('/api/admin/products/import/commit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ operations: importPreview.operations }) });
      if (!r.ok) throw new Error('Import failed');
      const res = await r.json();
      setImportResult(res);
      const fresh = await authFetch('/api/admin/products').then((x) => x.json());
      setProducts(fresh);
      toast.success(`Imported · ${res.created} created, ${res.updated} updated`);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setImporting(false); }
  };

  // ---- Dashboard ----
  return (
    <div className="min-h-screen bg-neutral-50">
      <Toaster theme="dark" position="top-center" />
      <header className="bg-black text-white px-6 lg:px-10 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="font-display tracking-[0.22em] text-sm">FORTUNE · CATALOG ADMIN</div>
        <div className="flex items-center gap-2">
          {['products', 'collections', 'orders', 'import', 'hero', 'featured', 'abandoned', 'settings'].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1.5 font-display tracking-[0.15em] text-xs ${tab === t ? 'text-[#FF6B00]' : 'text-white/60 hover:text-white'}`}
              data-testid={`admin-tab-${t}`}>{t.toUpperCase()}</button>
          ))}
          <Button onClick={logout} variant="ghost" className="text-white/70 hover:text-white gap-2" data-testid="admin-logout">
            <LogOut size={15} /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {tab === 'products' && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
                  className="pl-9 h-11 rounded-none border-neutral-300 text-neutral-900" data-testid="admin-product-search" />
              </div>
              <span className="text-sm text-neutral-500">{filtered.length} products</span>
              <div className="ml-auto flex items-center gap-2">
                <Button onClick={() => exportCsv(q ? 'filtered' : 'all')} variant="outline"
                  className="h-11 rounded-none border-neutral-300 text-neutral-900 font-display tracking-[0.12em] text-xs gap-2" data-testid="export-csv">
                  <Download size={14} /> EXPORT CSV
                </Button>
                <label className="h-11 inline-flex items-center gap-2 px-4 border border-neutral-300 cursor-pointer font-display tracking-[0.12em] text-xs hover:border-[#FF6B00]" data-testid="import-csv-label">
                  <Upload size={14} /> IMPORT CSV
                  <input type="file" accept=".csv,text/csv" className="hidden" data-testid="import-csv-input"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; runImportPreview(f); }} />
                </label>
                <Button onClick={() => setEditing({ _isNew: true, name: '', brand: '', category: '', price: 0, images: [], published: true })}
                  className="h-11 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em] text-xs gap-2"
                  data-testid="admin-new-product"><Plus size={15} /> NEW PRODUCT</Button>
              </div>
            </div>
            {selectedProducts.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-neutral-900 text-white mb-3" data-testid="product-bulk-bar">
                <span className="text-xs font-display tracking-[0.12em] mr-2">{selectedProducts.length} SELECTED</span>
                <Button onClick={() => setBulkEditOpen(true)} className="h-8 rounded-none bg-[#FF6B00] hover:bg-[#ff8534] text-black text-xs font-display tracking-[0.1em]" data-testid="bulk-edit-products">EDIT PRODUCTS</Button>
                <Button onClick={() => bulkAction('publish')} className="h-8 rounded-none bg-white/10 hover:bg-[#FF6B00] text-xs font-display tracking-[0.1em]" data-testid="bulk-publish">PUBLISH</Button>
                <Button onClick={() => bulkAction('unpublish')} className="h-8 rounded-none bg-white/10 hover:bg-[#FF6B00] text-xs font-display tracking-[0.1em]" data-testid="bulk-unpublish">HIDE</Button>
                <div className="flex items-center gap-1">
                  <Input type="number" value={bulkMarkup} onChange={(e) => setBulkMarkup(e.target.value)} placeholder="%"
                    className="h-8 w-16 rounded-none border-white/20 bg-white/10 text-white text-xs" data-testid="bulk-markup-input" />
                  <Button onClick={() => bulkAction('markup')} className="h-8 rounded-none bg-white/10 hover:bg-[#FF6B00] text-xs font-display tracking-[0.1em]" data-testid="bulk-markup-apply">APPLY MARKUP</Button>
                </div>
                <Button onClick={() => exportCsv('selected')} className="h-8 rounded-none bg-white/10 hover:bg-[#FF6B00] text-xs font-display tracking-[0.1em] gap-1" data-testid="bulk-export-selected"><Download size={12} /> EXPORT</Button>
                <Button onClick={() => setBulkDeleteOpen(true)} className="h-8 rounded-none bg-red-600 hover:bg-red-700 text-xs font-display tracking-[0.1em] ml-auto" data-testid="bulk-delete">DELETE</Button>
                <Button onClick={() => setSelectedProducts([])} variant="ghost" className="h-8 rounded-none text-xs text-white/70 hover:text-white hover:bg-transparent" data-testid="bulk-clear">Clear</Button>
              </div>
            )}
            <div className="bg-white border border-neutral-200 divide-y divide-neutral-100" data-testid="admin-product-list">
              {filtered.length > 0 && (
                <div className="flex items-center gap-4 px-4 py-2 bg-neutral-50">
                  <Checkbox
                    checked={selectedProducts.length > 0 && filtered.every((p) => selectedProducts.includes(p.id))}
                    onCheckedChange={(v) => setSelectedProducts(v ? filtered.map((p) => p.id) : [])}
                    data-testid="product-select-all" />
                  <span className="text-xs text-neutral-500">Select all {filtered.length}</span>
                </div>
              )}
              {filtered.map((p) => (
                <div key={p.id} className="flex items-center gap-4 px-4 py-3 hover:bg-neutral-50" data-testid={`admin-row-${p.id}`}>
                  <Checkbox checked={selectedProducts.includes(p.id)} onCheckedChange={() => toggleProductSelect(p.id)} data-testid={`product-select-${p.id}`} />
                  <img src={imgSrc(p.images?.[0])} alt="" className="h-12 w-12 object-cover bg-neutral-100 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-neutral-900 truncate">{p.name}
                      {p.published === false && <span className="ml-2 text-[9px] font-display tracking-[0.12em] px-1.5 py-0.5 bg-amber-100 text-amber-700 align-middle">DRAFT</span>}
                    </div>
                    <div className="text-xs text-neutral-500">{p.brand} · ${Number(p.price).toLocaleString()}</div>
                  </div>
                  <Switch checked={p.published !== false} onCheckedChange={() => togglePublish(p)}
                    className="data-[state=checked]:bg-[#FF6B00]" data-testid={`admin-publish-${p.id}`} />
                  <Button variant="outline" onClick={() => openPreview(p)}
                    className="rounded-none border-neutral-300 text-neutral-900 h-9 w-9 p-0" title="Preview on site"
                    data-testid={`admin-preview-${p.id}`}><ExternalLink size={15} /></Button>
                  <Button variant="outline" onClick={() => setEditing(recalcPricing({ ...p }))}
                    className="rounded-none border-neutral-300 text-neutral-900 h-9 text-xs font-display tracking-[0.12em]"
                    data-testid={`admin-edit-${p.id}`}>EDIT</Button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'import' && (
          <div className="max-w-3xl [&_input]:text-neutral-900" data-testid="admin-import">
            <div className="bg-white border border-neutral-200 p-6 mb-6">
              <div className="flex items-center gap-2 mb-1">
                <Download size={18} className="text-[#FF6B00]" />
                <h2 className="font-display tracking-[0.15em] text-neutral-900 text-lg">SCAN &amp; IMPORT PRODUCTS</h2>
              </div>
              <p className="text-sm text-neutral-500 mb-5">Paste a product page or a collection/category URL. We read the page, extract each product, and add them as <strong>unpublished drafts</strong> for you to review before publishing.</p>
              <Field label="PAGE URL">
                <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://supplier.com/products/…"
                  className="rounded-none border-neutral-300 h-11" data-testid="import-url" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="PAGE TYPE">
                  <Select value={importMode} onValueChange={setImportMode}>
                    <SelectTrigger className="rounded-none border-neutral-300 text-neutral-900" data-testid="import-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-detect</SelectItem>
                      <SelectItem value="single">Single product</SelectItem>
                      <SelectItem value="collection">Collection / category</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="MAX PRODUCTS">
                  <Input type="number" min={1} max={60} value={importMax} onChange={(e) => setImportMax(e.target.value)}
                    className="rounded-none border-neutral-300 h-10" data-testid="import-max" />
                </Field>
                <Field label="MARKUP % (OPTIONAL)">
                  <Input type="number" value={importMarkup} onChange={(e) => setImportMarkup(e.target.value)} placeholder="e.g. 40"
                    className="rounded-none border-neutral-300 h-10" data-testid="import-markup" disabled={importLeaveBlank} />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-600 mb-5 cursor-pointer">
                <Checkbox checked={importLeaveBlank} onCheckedChange={(v) => setImportLeaveBlank(!!v)} data-testid="import-leave-blank" />
                Leave price blank (I'll set prices myself)
              </label>
              <Button onClick={startImportScan} disabled={importScanning}
                className="h-11 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em] text-xs gap-2"
                data-testid="import-scan-btn">
                {importScanning ? <><Loader2 size={15} className="animate-spin" /> SCANNING…</> : <>SCAN PAGE</>}
              </Button>
              {importScanning && importJob?.progress?.message && (
                <div className="mt-4 text-sm text-neutral-500" data-testid="import-progress">
                  {importJob.progress.message}
                  {importJob.progress.total > 0 && ` (${importJob.progress.done}/${importJob.progress.total})`}
                </div>
              )}
            </div>

            {importJob?.status === 'done' && (
              <div className="bg-white border border-neutral-200" data-testid="import-results">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200">
                  <span className="text-sm text-neutral-900 font-display tracking-[0.1em]">
                    {(importJob.candidates || []).length} PRODUCT(S) FOUND · {importSelected.length} SELECTED
                  </span>
                  <Button onClick={commitImport} disabled={importCommitting || importSelected.length === 0}
                    className="ml-auto h-9 rounded-none bg-[#FF6B00] hover:bg-[#ff8124] font-display tracking-[0.12em] text-xs gap-2"
                    data-testid="import-commit-btn">
                    {importCommitting ? <Loader2 size={14} className="animate-spin" /> : `IMPORT ${importSelected.length} AS DRAFTS`}
                  </Button>
                </div>
                {(importJob.candidates || []).length === 0 && (
                  <div className="px-4 py-10 text-center text-sm text-neutral-400">No products could be extracted from that URL.</div>
                )}
                {(importJob.candidates || []).map((c, idx) => (
                  <div key={idx} className="flex items-center gap-4 px-4 py-3 border-b border-neutral-100" data-testid={`import-candidate-${idx}`}>
                    <Checkbox checked={importSelected.includes(idx)} onCheckedChange={() => toggleImportSelect(idx)} data-testid={`import-select-${idx}`} />
                    <img src={imgSrc(c.images?.[0])} alt="" className="h-14 w-14 object-cover bg-neutral-100 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-neutral-900 truncate">{c.name}</div>
                      <div className="text-xs text-neutral-500">
                        {[c.brand, c.category].filter(Boolean).join(' · ')}
                        {c.price != null && ` · ${c.currency || '$'}${Number(c.price).toLocaleString()}`}
                        {c.variants?.length > 0 && ` · ${c.variants.length} variant(s)`}
                      </div>
                    </div>
                    <a href={c.source_url} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-[#FF6B00]" title="View source" data-testid={`import-source-${idx}`}>
                      <ExternalLink size={16} />
                    </a>
                  </div>
                ))}
                {(importJob.errors || []).length > 0 && (
                  <div className="px-4 py-3 text-xs text-red-500" data-testid="import-errors">
                    {importJob.errors.length} page(s) could not be read (site may block scraping or load via JavaScript).
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'settings' && (
          <div className="max-w-2xl" data-testid="admin-settings">
            <div className="bg-white border border-neutral-200 p-6">
              <h2 className="font-display tracking-[0.15em] text-neutral-900 text-lg mb-1">PAYMENTS & CURRENCY</h2>
              <p className="text-sm text-neutral-500 mb-5">Your store displays prices in <strong>USD</strong>, but cards are charged in <strong>CAD</strong> via Helcim. Set the USD→CAD rate used to convert the cart total at checkout.</p>
              <Field label="USD → CAD EXCHANGE RATE">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-neutral-500">1 USD =</span>
                  <Input type="number" step="0.01" value={fxRate} onChange={(e) => setFxRate(e.target.value)}
                    className="rounded-none border-neutral-300 text-neutral-900 w-32 h-11" data-testid="fx-rate-input" />
                  <span className="text-sm text-neutral-500">CAD</span>
                </div>
              </Field>
              {fxRate && !Number.isNaN(Number(fxRate)) && (
                <p className="text-xs text-neutral-500 mb-4" data-testid="fx-example">
                  Example: a <strong>$100.00 USD</strong> cart charges the customer <strong className="text-neutral-800">CA${(100 * Number(fxRate)).toFixed(2)}</strong>.
                </p>
              )}
              <Button onClick={saveFxRate} disabled={fxSaving}
                className="h-11 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em] text-xs gap-2" data-testid="fx-save-btn">
                {fxSaving ? <Loader2 size={15} className="animate-spin" /> : 'SAVE RATE'}
              </Button>
              <p className="text-[11px] text-neutral-400 mt-5 leading-relaxed">
                Tip: build in a small buffer (e.g. set slightly higher than the mid-market rate) to cover processor FX spread. Update this whenever the exchange rate shifts.
              </p>
            </div>

            <div className="bg-white border border-neutral-200 p-6 mt-6" data-testid="admin-ordernum">
              <h2 className="font-display tracking-[0.15em] text-neutral-900 text-lg mb-1">ORDER NUMBERS</h2>
              <p className="text-sm text-neutral-500 mb-5">Controls how <strong>new</strong> order numbers are generated. Existing numbers never change, and the starting number can&rsquo;t be set below what&rsquo;s already been used.</p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="PREFIX"><Input value={ordernum.prefix} onChange={(e) => setOrdernum((s) => ({ ...s, prefix: e.target.value }))} className="rounded-none border-neutral-300 h-11" data-testid="ordernum-prefix" /></Field>
                <Field label="SUFFIX (optional)"><Input value={ordernum.suffix} onChange={(e) => setOrdernum((s) => ({ ...s, suffix: e.target.value }))} className="rounded-none border-neutral-300 h-11" data-testid="ordernum-suffix" /></Field>
                <Field label="STARTING NUMBER"><Input type="number" value={ordernum.start} onChange={(e) => setOrdernum((s) => ({ ...s, start: e.target.value }))} className="rounded-none border-neutral-300 h-11" data-testid="ordernum-start" /></Field>
                <Field label="ZERO-PADDING (digits)"><Input type="number" min="1" max="8" value={ordernum.padding} onChange={(e) => setOrdernum((s) => ({ ...s, padding: e.target.value }))} className="rounded-none border-neutral-300 h-11" data-testid="ordernum-padding" /></Field>
              </div>
              <p className="text-xs text-neutral-500 mb-4" data-testid="ordernum-preview">
                Next order will be <strong className="text-neutral-800">{`${ordernum.prefix || ''}${String(Math.max(Number(ordernum.start) || 0, (ordernum.last_used || 0) + 1)).padStart(Number(ordernum.padding) || 1, '0')}${ordernum.suffix || ''}`}</strong>
                {ordernum.last_used ? <span className="text-neutral-400"> · highest used so far: {ordernum.last_used}</span> : null}
              </p>
              <Button onClick={saveOrdernum} disabled={ordernumSaving}
                className="h-11 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em] text-xs gap-2" data-testid="ordernum-save-btn">
                {ordernumSaving ? <Loader2 size={15} className="animate-spin" /> : 'SAVE ORDER NUMBERS'}
              </Button>
            </div>
          </div>
        )}

        {tab === 'hero' && (
          <div className="max-w-3xl" data-testid="admin-hero">
            <div className="bg-white border border-neutral-200 p-6 mb-6">
              <h2 className="font-display tracking-[0.15em] text-neutral-900 text-lg mb-1">HOMEPAGE HEADLINE</h2>
              <p className="text-sm text-neutral-500 mb-5">The kicker line, big headline and paragraph shown over the hero slideshow.</p>
              <Field label="KICKER (small line above headline)">
                <Input value={hero.headline.kicker} onChange={(e) => setHeadlineField('kicker', e.target.value)}
                  className="rounded-none border-neutral-300 text-neutral-900 h-11" data-testid="hero-kicker-input" />
              </Field>
              <Field label="HEADLINE (press Enter for a new line)">
                <Textarea value={hero.headline.title} onChange={(e) => setHeadlineField('title', e.target.value)} rows={3}
                  className="rounded-none border-neutral-300 text-neutral-900" data-testid="hero-title-input" />
              </Field>
              <Field label="SUBTEXT (paragraph beneath headline)">
                <Textarea value={hero.headline.subtext} onChange={(e) => setHeadlineField('subtext', e.target.value)} rows={3}
                  className="rounded-none border-neutral-300 text-neutral-900" data-testid="hero-subtext-input" />
              </Field>
            </div>

            <div className="bg-white border border-neutral-200 p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-display tracking-[0.15em] text-neutral-900 text-lg">HERO SLIDES</h2>
                <Button onClick={addSlide} variant="outline" className="rounded-none border-neutral-300 text-neutral-900 h-9 text-xs font-display tracking-[0.12em] gap-1" data-testid="hero-add-slide">
                  <Plus size={14} /> ADD SLIDE
                </Button>
              </div>
              <p className="text-sm text-neutral-500 mb-5">Background image, caption, button text and link per slide. Leave empty to keep the built-in default slideshow.</p>
              {hero.slides.length === 0 && (
                <p className="text-sm text-neutral-400 py-6 text-center border border-dashed border-neutral-300" data-testid="hero-no-slides">No custom slides — the homepage shows the built-in slideshow.</p>
              )}
              <div className="space-y-5">
                {hero.slides.map((s, idx) => (
                  <div key={idx} className="border border-neutral-200 p-4 flex gap-4" data-testid={`hero-slide-${idx}`}>
                    <div className="w-40 flex-shrink-0">
                      <div className="aspect-video bg-neutral-100 border border-neutral-200 overflow-hidden mb-2">
                        {s.image
                          ? <img src={imgSrc(s.image)} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-[10px] text-neutral-400">No image</div>}
                      </div>
                      <label className="flex items-center justify-center gap-2 px-3 h-9 border border-neutral-300 cursor-pointer text-xs font-display tracking-[0.12em] hover:border-[#FF6B00]" data-testid={`hero-upload-label-${idx}`}>
                        {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} UPLOAD
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadHeroImage(idx, e.target.files?.[0])} data-testid={`hero-upload-input-${idx}`} />
                      </label>
                    </div>
                    <div className="flex-1 min-w-0 space-y-3">
                      <Input value={s.label} onChange={(e) => setSlideField(idx, 'label', e.target.value)} placeholder="Caption e.g. Tesla · Highland Carbon"
                        className="rounded-none border-neutral-300 text-neutral-900 h-10" data-testid={`hero-slide-label-${idx}`} />
                      <div className="grid grid-cols-2 gap-3">
                        <Input value={s.ctaLabel} onChange={(e) => setSlideField(idx, 'ctaLabel', e.target.value)} placeholder="Button text e.g. SHOP MODEL 3"
                          className="rounded-none border-neutral-300 text-neutral-900 h-10" data-testid={`hero-slide-ctalabel-${idx}`} />
                        <Input value={s.ctaTo} onChange={(e) => setSlideField(idx, 'ctaTo', e.target.value)} placeholder="Link e.g. /collections/tesla-model-3-highland"
                          className="rounded-none border-neutral-300 text-neutral-900 h-10" data-testid={`hero-slide-ctato-${idx}`} />
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button type="button" onClick={() => moveSlide(idx, -1)} disabled={idx === 0} aria-label="Move up"
                        className="h-8 w-8 flex items-center justify-center border border-neutral-300 text-neutral-600 hover:border-[#FF6B00] disabled:opacity-30" data-testid={`hero-slide-up-${idx}`}><ChevronUp size={14} /></button>
                      <button type="button" onClick={() => moveSlide(idx, 1)} disabled={idx === hero.slides.length - 1} aria-label="Move down"
                        className="h-8 w-8 flex items-center justify-center border border-neutral-300 text-neutral-600 hover:border-[#FF6B00] disabled:opacity-30" data-testid={`hero-slide-down-${idx}`}><ChevronDown size={14} /></button>
                      <button type="button" onClick={() => removeSlide(idx)} aria-label="Remove slide"
                        className="h-8 w-8 flex items-center justify-center border border-neutral-300 text-red-500 hover:border-red-500" data-testid={`hero-slide-remove-${idx}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={saveHero} disabled={heroSaving} className="mt-6 h-11 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em] text-xs gap-2" data-testid="hero-save-btn">
                {heroSaving ? <Loader2 size={15} className="animate-spin" /> : 'SAVE HERO'}
              </Button>
            </div>
          </div>
        )}

        {tab === 'featured' && (
          <div data-testid="admin-featured">
            <div className="bg-white border border-neutral-200 p-5 mb-4 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display tracking-[0.15em] text-neutral-900 text-lg mb-1">FEATURED PRODUCTS</h2>
                <p className="text-sm text-neutral-500 max-w-xl">These products show in the homepage &ldquo;Parts That Perform&rdquo; carousel. Drag to set the order, or remove one. To add a product, turn on <strong>Featured on homepage</strong> in the product editor or the bulk editor. Showing up to {featuredCap}.</p>
              </div>
              <Button onClick={saveFeatured} disabled={featuredSaving} className="h-11 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em] text-xs gap-2" data-testid="featured-save">
                {featuredSaving ? <Loader2 size={15} className="animate-spin" /> : 'SAVE ORDER'}
              </Button>
            </div>
            {featuredItems.length === 0 ? (
              <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-400" data-testid="featured-empty">
                No products are marked featured yet. The homepage shows a curated fallback set until you mark your own.
              </div>
            ) : (
              <div className="bg-white border border-neutral-200 divide-y divide-neutral-100" data-testid="featured-list">
                {featuredItems.map((p, i) => (
                  <div key={p.id} draggable
                    onDragStart={() => setFeaturedDrag(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (featuredDrag !== null) moveFeatured(featuredDrag, i); setFeaturedDrag(null); }}
                    className={`flex items-center gap-4 px-4 py-3 cursor-move ${featuredDrag === i ? 'bg-orange-50' : 'hover:bg-neutral-50'}`}
                    data-testid={`featured-row-${p.id}`}>
                    <span className="text-neutral-300 select-none"><GripVertical size={16} /></span>
                    <span className="text-xs text-neutral-400 w-5 text-center">{i + 1}</span>
                    <img src={imgSrc(p.image)} alt="" className="h-12 w-12 object-cover bg-neutral-100 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-neutral-900 truncate">{p.name}
                        {p.published === false && <span className="ml-2 text-[9px] font-display tracking-[0.12em] px-1.5 py-0.5 bg-amber-100 text-amber-700 align-middle">DRAFT</span>}
                      </div>
                      <div className="text-xs text-neutral-500">{(p.collectionName || p.brand || '—')} · ${Number(p.price || 0).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveFeatured(i, i - 1)} disabled={i === 0} className="h-8 w-8 flex items-center justify-center text-neutral-400 hover:text-[#FF6B00] disabled:opacity-30" data-testid={`featured-up-${p.id}`}><ChevronUp size={16} /></button>
                      <button onClick={() => moveFeatured(i, i + 1)} disabled={i === featuredItems.length - 1} className="h-8 w-8 flex items-center justify-center text-neutral-400 hover:text-[#FF6B00] disabled:opacity-30" data-testid={`featured-down-${p.id}`}><ChevronDown size={16} /></button>
                      <button onClick={() => removeFeatured(p.id)} className="h-8 w-8 flex items-center justify-center text-neutral-400 hover:text-red-600" title="Remove from homepage" data-testid={`featured-remove-${p.id}`}><X size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {featuredItems.length > featuredCap && (
              <p className="text-xs text-amber-600 mt-2">Only the first {featuredCap} will show on the homepage.</p>
            )}
          </div>
        )}


        {tab === 'abandoned' && (
          <div data-testid="admin-abandoned">
            <div className="bg-white border border-neutral-200 p-5 mb-4">
              <h2 className="font-display tracking-[0.15em] text-neutral-900 text-lg mb-1">ABANDONED CHECKOUTS</h2>
              <p className="text-sm text-neutral-500">Shoppers who opened the payment window but didn&rsquo;t finish paying (older than 45 minutes). You&rsquo;re emailed once per checkout automatically.</p>
            </div>
            {abandoned.length === 0 ? (
              <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-400" data-testid="abandoned-empty">No abandoned checkouts right now.</div>
            ) : (
              <div className="bg-white border border-neutral-200 divide-y divide-neutral-100" data-testid="abandoned-list">
                {abandoned.map((a) => (
                  <div key={a.checkoutToken} className="px-4 py-3" data-testid={`abandoned-${a.checkoutToken}`}>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="text-sm text-neutral-900 font-medium truncate">
                          {a.name || 'Unknown'}
                          {a.notified && <span className="ml-2 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5">EMAILED</span>}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {a.email ? <a href={`mailto:${a.email}`} className="text-[#FF6B00] hover:underline">{a.email}</a> : '—'}
                          {a.phone ? <> · <a href={`tel:${a.phone}`} className="hover:underline">{a.phone}</a></> : null}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-neutral-900">${fmtMoney(a.usd_total)} USD</div>
                        <div className="text-xs text-neutral-500">{a.total_items} item{a.total_items !== 1 ? 's' : ''} · {a.created_at ? new Date(a.created_at).toLocaleString() : ''}</div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(a.items || []).map((li, i) => (
                        <span key={i} className="text-[11px] bg-neutral-100 text-neutral-600 px-2 py-1">{li.name}{li.variant ? ` · ${li.variant}` : ''} × {li.quantity}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'collections' && (
          <div className="bg-white border border-neutral-200 divide-y divide-neutral-100" data-testid="admin-collection-list">
            {collections.map((c) => (
              <div key={c.id} className="flex items-center gap-4 px-4 py-3 hover:bg-neutral-50">
                <img src={imgSrc(c.image)} alt="" className="h-12 w-16 object-cover bg-neutral-100 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-neutral-900 truncate">{c.name}</div>
                  <div className="text-xs text-neutral-500">{c.brand} · {c.years || '—'}</div>
                </div>
                <Button variant="outline" onClick={() => setEditing({ ...c, _type: 'collection' })}
                  className="rounded-none border-neutral-300 text-neutral-900 h-9 text-xs font-display tracking-[0.12em]"
                  data-testid={`admin-edit-col-${c.id}`}>EDIT</Button>
              </div>
            ))}
          </div>
        )}

        {tab === 'orders' && (
          <div data-testid="admin-order-list">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display tracking-[0.15em] text-neutral-900 text-lg">ORDERS</h2>
              <Button onClick={() => { setEditingOrder(null); setBuilderOpen(true); }}
                className="rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.12em] text-xs gap-1" data-testid="admin-create-order">
                <Plus size={14} /> CREATE ORDER
              </Button>
            </div>
            {orders.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-2 bg-white border border-neutral-200 border-b-0">
                <Checkbox checked={selectedOrders.length === orders.length && orders.length > 0} onCheckedChange={toggleSelectAll} data-testid="admin-orders-select-all" />
                <span className="text-xs text-neutral-500">
                  {selectedOrders.length > 0 ? `${selectedOrders.length} selected` : 'Select all'}
                </span>
                {selectedOrders.length > 0 && (
                  <Button variant="outline" onClick={() => setDeleteTarget([...selectedOrders])}
                    className="ml-auto rounded-none border-red-300 text-red-600 hover:bg-red-50 h-8 text-xs font-display tracking-[0.12em]"
                    data-testid="admin-orders-bulk-delete">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> DELETE ({selectedOrders.length})
                  </Button>
                )}
              </div>
            )}
            <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
              {orders.length === 0 && <div className="px-4 py-10 text-center text-sm text-neutral-400">No orders yet.</div>}
              {orders.map((o) => (
                <div key={o.id} className="flex items-center gap-4 px-4 py-3 hover:bg-neutral-50" data-testid={`admin-order-${o.id}`}>
                  <Checkbox checked={selectedOrders.includes(o.id)} onCheckedChange={() => toggleOrderSelect(o.id)} data-testid={`admin-order-select-${o.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-neutral-900">{orderNo(o)} · <span className="text-neutral-600">{o.customer_name}</span></div>
                    <div className="text-xs text-neutral-500 flex items-center gap-2 flex-wrap mt-0.5">
                      <span>{new Date(o.created_at).toLocaleDateString()} · ${Number(o.total).toLocaleString()} · {o.total_items} item(s)</span>
                      <PaymentBadge order={o} compact />
                    </div>
                  </div>
                  <span className="text-[10px] font-display tracking-[0.15em] px-2 py-1 bg-neutral-100 text-neutral-700">{fmtStatus(o.status)}</span>
                  <Button variant="outline" onClick={() => setViewingOrder(o)}
                    className="rounded-none border-neutral-300 text-neutral-900 h-9 text-xs font-display tracking-[0.12em]"
                    data-testid={`admin-view-order-${o.id}`}>VIEW</Button>
                  <Button variant="outline" onClick={() => setDeleteTarget([o.id])}
                    className="rounded-none border-red-200 text-red-500 hover:bg-red-50 h-9 w-9 p-0"
                    data-testid={`admin-delete-order-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Orders detail drawer */}
      <Sheet open={!!viewingOrder} onOpenChange={(o) => !o && setViewingOrder(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white text-neutral-900">
          {viewingOrder && (
            <>
              <SheetHeader className="mb-4"><SheetTitle className="font-display tracking-[0.15em]">ORDER {orderNo(viewingOrder)}</SheetTitle></SheetHeader>
              <div className="text-xs text-neutral-500 mb-4">{new Date(viewingOrder.created_at).toLocaleString()}</div>
              <Field label="STATUS">
                <Select value={canonStatus(viewingOrder.status)} onValueChange={(v) => openStatusChange(viewingOrder, v)}>
                  <SelectTrigger className="rounded-none border-neutral-300" data-testid="order-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{ORDER_PIPELINE.map(([k, l, e]) => <SelectItem key={k} value={k}>{l}{e ? ' ✉' : ''}</SelectItem>)}</SelectContent>
                </Select>
                {viewingOrder.estimated_timeframe && (
                  <div className="text-xs text-neutral-500 mt-1.5" data-testid="order-timeframe">Estimated timeframe: <span className="text-neutral-800">{viewingOrder.estimated_timeframe}</span></div>
                )}
                {viewingOrder.status === 'shipped' && (viewingOrder.tracking_number || viewingOrder.tracking_url) && (
                  <div className="text-xs text-neutral-500 mt-1">Tracking: <span className="text-neutral-800">{[viewingOrder.carrier, viewingOrder.tracking_number].filter(Boolean).join(' · ') || 'link'}</span></div>
                )}
              </Field>
              <div className="border border-neutral-200 p-3 mb-4 text-sm">
                <div className="text-[11px] font-display tracking-[0.18em] text-neutral-500 mb-1">CUSTOMER</div>
                <div className="text-neutral-900">{viewingOrder.customer_name}</div>
                <div className="text-neutral-600">{viewingOrder.customer_email}</div>
                <div className="text-neutral-600">{viewingOrder.customer_phone}</div>
                {viewingOrder.shipping && (
                  <div className="text-neutral-600 mt-2">{[viewingOrder.shipping.address_1, viewingOrder.shipping.address_2, viewingOrder.shipping.city, viewingOrder.shipping.state, viewingOrder.shipping.postcode, viewingOrder.shipping.country].filter(Boolean).join(', ')}</div>
                )}
              </div>
              <div className="text-[11px] font-display tracking-[0.18em] text-neutral-500 mb-2">ITEMS</div>
              {(viewingOrder.line_items || []).map((li, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-neutral-100 text-sm">
                  <img src={imgSrc(li.image)} alt="" className="h-10 w-10 object-cover bg-neutral-100" />
                  <div className="flex-1 min-w-0"><div className="truncate text-neutral-900">{li.name}</div><div className="text-xs text-neutral-500">{li.variant} · Qty {li.quantity}</div></div>
                  <div className="text-neutral-900">${Number(li.total).toLocaleString()}</div>
                </div>
              ))}
              <div className="flex justify-between py-3 font-display text-lg"><span>TOTAL</span><span>${Number(viewingOrder.total).toLocaleString()} {viewingOrder.currency}</span></div>
              <div className="border border-neutral-200 p-3 mb-4 text-sm" data-testid="order-payment-block">
                <div className="text-[11px] font-display tracking-[0.18em] text-neutral-500 mb-2">PAYMENT</div>
                <PaymentBadge order={viewingOrder} />
                {viewingOrder.payment_flow === 'helcim_card' && (
                  <div className="text-xs text-neutral-600 mt-2 space-y-0.5">
                    <div>Charged: <strong className="text-neutral-900">CA${fmtMoney(viewingOrder.amount_charged_cad)}</strong>{viewingOrder.fx_rate ? ` (rate ${viewingOrder.fx_rate})` : ''}</div>
                    <div>Order total: ${Number(viewingOrder.total).toLocaleString()} USD</div>
                    {viewingOrder.helcim_transaction_id && <div>Helcim txn: <span className="font-mono">{viewingOrder.helcim_transaction_id}</span></div>}
                    {viewingOrder.helcim_card_number && <div>Card: {viewingOrder.helcim_card_number}</div>}
                  </div>
                )}
              </div>
              {viewingOrder.customer_note && <p className="text-sm text-neutral-600 italic mb-3">"{viewingOrder.customer_note}"</p>}
              <Field label="ADD INTERNAL NOTE">
                <div className="flex gap-2">
                  <Input id="order-note-input" placeholder="Note…" className="rounded-none border-neutral-300 h-9 text-sm" data-testid="order-note-input"
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value.trim()) { updateOrder(viewingOrder.id, { note: e.target.value.trim() }); e.target.value = ''; } }} />
                </div>
              </Field>
              {(viewingOrder.notes || []).map((n, i) => (
                <div key={i} className="text-xs text-neutral-600 border-l-2 border-neutral-200 pl-2 mb-1">{n.text} <span className="text-neutral-400">— {new Date(n.at).toLocaleDateString()}</span></div>
              ))}
              {viewingOrder.payment_request && (
                <div className="border border-[#FF6B00]/30 bg-[#FF6B00]/5 p-3 mb-3 text-xs text-neutral-700" data-testid="order-payreq-info">
                  Payment request sent · locked CA${fmtMoney(viewingOrder.payment_request.amount_cad)} (${Number(viewingOrder.payment_request.usd_total).toLocaleString()} USD).
                  {viewingOrder.payment_request.expires_at && <> Link expires {new Date(viewingOrder.payment_request.expires_at).toLocaleDateString()}.</>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button onClick={() => { setEditingOrder(viewingOrder); setBuilderOpen(true); }} variant="outline"
                  className="rounded-none border-neutral-300 text-neutral-900 font-display tracking-[0.15em] text-xs" data-testid="order-edit">EDIT ORDER</Button>
                <Button onClick={() => sendPaymentRequest(viewingOrder)} disabled={payReqSending}
                  className="rounded-none bg-[#FF6B00] hover:bg-[#E25500] text-white font-display tracking-[0.15em] text-xs gap-1" data-testid="order-send-payment">
                  {payReqSending ? <Loader2 size={13} className="animate-spin" /> : (viewingOrder.payment_request ? 'RESEND PAY LINK' : 'SEND PAYMENT REQUEST')}
                </Button>
              </div>
              <Button onClick={() => resendOrderEmail(viewingOrder.id)} variant="outline" className="w-full rounded-none border-neutral-300 text-neutral-900 font-display tracking-[0.15em] text-xs mt-2" data-testid="order-resend">RESEND CURRENT STATUS EMAIL</Button>
              <Button onClick={() => setDeleteTarget([viewingOrder.id])} variant="outline" className="w-full rounded-none border-red-300 text-red-600 hover:bg-red-50 font-display tracking-[0.15em] text-xs mt-2" data-testid="order-delete">
                <Trash2 className="h-4 w-4 mr-2" /> DELETE ORDER
              </Button>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Status change confirmation + notify dialog */}
      <AlertDialog open={!!statusChange} onOpenChange={(o) => !o && setStatusChange(null)}>
        <AlertDialogContent data-testid="status-change-dialog" className="bg-white text-neutral-900 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-900">
              Move to “{statusChange ? STATUS_LABEL[statusChange.stage] : ''}”?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-500">
              {statusChange && STATUS_EMAILS.has(statusChange.stage)
                ? 'This stage can email the customer. Review the toggle and optional note below.'
                : 'This is an internal stage — no customer email is sent.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {statusChange && (
            <div className="space-y-3">
              {STATUS_EMAILS.has(statusChange.stage) && (
                <>
                  <label className="flex items-center justify-between gap-3 border border-neutral-200 px-3 py-2.5">
                    <span className="text-sm text-neutral-800">Notify customer by email</span>
                    <Switch checked={statusChange.notify} onCheckedChange={(v) => setStatusChange((s) => ({ ...s, notify: v }))}
                      className="data-[state=checked]:bg-[#FF6B00]" data-testid="status-notify-toggle" />
                  </label>
                  {statusChange.order.status_emails_sent && statusChange.order.status_emails_sent[statusChange.stage] && (
                    <p className="text-[11px] text-amber-600" data-testid="status-already-sent">
                      Already emailed for this stage — it won’t re-send. Use “Resend current status email” if you need to.
                    </p>
                  )}
                  {(statusChange.stage === 'awaiting_payment' || statusChange.stage === 'in_production') && (
                    <Field label="ESTIMATED TIMEFRAME (shown in email — optional)">
                      <Input value={statusChange.timeframe} onChange={(e) => setStatusChange((s) => ({ ...s, timeframe: e.target.value }))}
                        placeholder="e.g. 4–6 weeks or ships week of Jun 30"
                        className="rounded-none border-neutral-300 h-10 text-sm" data-testid="status-timeframe-input" />
                    </Field>
                  )}
                  {statusChange.stage === 'shipped' && (
                    <div className="grid grid-cols-1 gap-2">
                      <Input value={statusChange.carrier} onChange={(e) => setStatusChange((s) => ({ ...s, carrier: e.target.value }))}
                        placeholder="Carrier (e.g. UPS)" className="rounded-none border-neutral-300 h-10 text-sm" data-testid="status-carrier-input" />
                      <Input value={statusChange.tracking_number} onChange={(e) => setStatusChange((s) => ({ ...s, tracking_number: e.target.value }))}
                        placeholder="Tracking number" className="rounded-none border-neutral-300 h-10 text-sm" data-testid="status-tracking-input" />
                      <Input value={statusChange.tracking_url} onChange={(e) => setStatusChange((s) => ({ ...s, tracking_url: e.target.value }))}
                        placeholder="Tracking URL (optional)" className="rounded-none border-neutral-300 h-10 text-sm" data-testid="status-trackingurl-input" />
                    </div>
                  )}
                  <Field label="PERSONAL NOTE (added into the email — optional)">
                    <Textarea value={statusChange.note} onChange={(e) => setStatusChange((s) => ({ ...s, note: e.target.value }))} rows={3}
                      placeholder="A short message to the customer…" className="rounded-none border-neutral-300 text-sm" data-testid="status-note-input" />
                  </Field>
                </>
              )}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="status-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange}
              className="rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.12em] text-xs" data-testid="status-confirm">
              {statusChange && statusChange.notify && STATUS_EMAILS.has(statusChange.stage) ? 'SAVE & SEND EMAIL' : 'SAVE STATUS'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="order-delete-dialog" className="bg-white text-neutral-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-900">Delete {deleteTarget && deleteTarget.length > 1 ? `${deleteTarget.length} orders` : 'this order'}?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-500">
              This permanently removes {deleteTarget && deleteTarget.length > 1 ? 'these orders' : 'this order'} from the database. This action cannot be undone and does not notify the customer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-neutral-900 border-neutral-300" data-testid="order-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteOrders} className="bg-red-600 hover:bg-red-700 text-white" data-testid="order-delete-confirm">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog" className="bg-white text-neutral-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-900">Delete {selectedProducts.length} product(s)?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-500">
              This permanently removes the selected products from the catalog. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-neutral-900 border-neutral-300" data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => bulkAction('delete')} className="bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-delete-confirm">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Editor drawer */}
      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white text-neutral-900 [&_input]:text-neutral-900 [&_textarea]:text-neutral-900">
          {editing && editing._type === 'collection' ? (
            <>
              <SheetHeader className="mb-4"><SheetTitle className="font-display tracking-[0.15em]">EDIT COLLECTION</SheetTitle></SheetHeader>
              <Field label="NAME"><Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-none border-neutral-300" data-testid="col-name" /></Field>
              <Field label="MODEL YEARS"><Input value={editing.years || ''} onChange={(e) => setEditing({ ...editing, years: e.target.value })} className="rounded-none border-neutral-300" data-testid="col-years" /></Field>
              <Field label="BANNER IMAGE">
                {editing.image && <img src={imgSrc(editing.image)} alt="" className="w-full h-40 object-cover mb-2 bg-neutral-100" />}
                <div className="flex gap-2">
                  <label className="flex items-center gap-2 px-3 h-9 border border-neutral-300 cursor-pointer text-xs font-display tracking-[0.12em] hover:border-[#FF6B00]" data-testid="col-upload-label">
                    {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} UPLOAD
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCollectionImage(e.target.files?.[0])} data-testid="col-upload-input" />
                  </label>
                  <Input value={editing.image || ''} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="…or paste image URL" className="rounded-none border-neutral-300 h-9 text-sm" data-testid="col-image" />
                </div>
              </Field>
              <Field label="COLLECTION DESCRIPTION">
                <RichTextEditor
                  value={editing.descriptionHtml || ''}
                  onChange={(html) => setEditing({ ...editing, descriptionHtml: html })}
                  placeholder="Intro copy shown on the collection page (great for SEO)."
                  testId="col-description"
                />
              </Field>
              <Field label="SEO PAGE TITLE"><Input value={editing.seoTitle || ''} onChange={(e) => setEditing({ ...editing, seoTitle: e.target.value })} className="rounded-none border-neutral-300" data-testid="col-seo-title" /></Field>
              <Field label="SEO META DESCRIPTION"><Textarea value={editing.seoDescription || ''} onChange={(e) => setEditing({ ...editing, seoDescription: e.target.value })} rows={2} className="rounded-none border-neutral-300" data-testid="col-seo-desc" /></Field>
              <Button disabled={saving} data-testid="col-save"
                onClick={async () => { setSaving(true); try { const b = { ...editing }; delete b._type; const r = await authFetch(`/api/admin/collections/${editing.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b) }); if (!r.ok) throw new Error('Save failed'); toast.success('Saved'); setEditing(null); await load(); } catch (e) { toast.error(e.message); } finally { setSaving(false); } }}
                className="w-full h-11 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em]">{saving ? <Loader2 className="animate-spin" size={16} /> : 'SAVE'}</Button>
            </>
          ) : editing ? (
            <>
              <SheetHeader className="mb-4"><SheetTitle className="font-display tracking-[0.15em]">{editing._isNew ? 'NEW PRODUCT' : 'EDIT PRODUCT'}</SheetTitle></SheetHeader>
              <Field label="NAME"><Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-none border-neutral-300" data-testid="prod-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="BRAND"><Input value={editing.brand || ''} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} className="rounded-none border-neutral-300" data-testid="prod-brand" /></Field>
                <Field label="PART CATEGORY"><Input value={editing.category || ''} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-none border-neutral-300" data-testid="prod-category" /></Field>
              </div>
              <Field label="VEHICLE / COLLECTION">
                <Select value={editing.collectionId || ''} onValueChange={(v) => setEditing({ ...editing, collectionId: v })}>
                  <SelectTrigger className="rounded-none border-neutral-300" data-testid="prod-collection"><SelectValue placeholder="Select a collection" /></SelectTrigger>
                  <SelectContent className="max-h-72">{collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="PRICE (USD)"><Input type="number" value={editing.price ?? ''} onChange={(e) => setEditing((prev) => recalcPricing({ ...prev, price: e.target.value === '' ? 0 : Number(e.target.value) }))} className="rounded-none border-neutral-300" data-testid="prod-price" /></Field>
                <Field label="COMPARE-AT (SALE)"><Input type="number" value={editing.compareAtPrice ?? ''} onChange={(e) => setEditing({ ...editing, compareAtPrice: e.target.value === '' ? null : Number(e.target.value) })} className="rounded-none border-neutral-300" data-testid="prod-compare" /></Field>
              </div>
              <Field label="COST PER ITEM (USD)">
                <Input type="number" value={editing.cost ?? ''} onChange={(e) => setEditing((prev) => recalcPricing({ ...prev, cost: e.target.value === '' ? null : Number(e.target.value), costManual: true }))} placeholder="What you pay your supplier" className="rounded-none border-neutral-300" data-testid="prod-cost" />
                <MarginRow price={editing.price} cost={editing.cost} />
              </Field>
              <Field label="AUTO-COST · COST = LIST PRICE − __%">
                <div className="flex items-center gap-2">
                  <Input type="number" value={editing.costPct ?? ''} onChange={(e) => setCostPct(e.target.value === '' ? '' : Number(e.target.value))} placeholder="e.g. 40" className="rounded-none border-neutral-300 w-28" data-testid="prod-cost-pct" />
                  <span className="text-[11px] text-neutral-500">
                    {hasPct(editing.costPct)
                      ? `Cost auto-filled ${editing.costPct}% below each price. Type a cost to override that one.`
                      : 'Leave blank to enter costs manually.'}
                  </span>
                </div>
              </Field>
              <Field label="PRICE ENDING (CHARM)">
                <div className="flex items-center gap-2">
                  <Select value={editing.priceEnding || 'exact'} onValueChange={(v) => setEditing((prev) => recalcPricing({ ...prev, priceEnding: v }))}>
                    <SelectTrigger className="rounded-none border-neutral-300 w-36 text-neutral-900" data-testid="prod-price-ending"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exact">Exact</SelectItem>
                      <SelectItem value="99">.99</SelectItem>
                      <SelectItem value="95">.95</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-[11px] text-neutral-500">Rounds auto-calculated variant prices to a clean retail ending.</span>
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="STOCK STATUS">
                  <Select value={editing.status || 'in-stock'} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="rounded-none border-neutral-300" data-testid="prod-stock"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="in-stock">In stock</SelectItem><SelectItem value="out-of-stock">Out of stock</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="SKU"><Input value={editing.sku || ''} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} className="rounded-none border-neutral-300" data-testid="prod-sku" /></Field>
              </div>
              <Field label="DESCRIPTION">
                <RichTextEditor
                  value={editing.descriptionHtml || ''}
                  onChange={(html) => setEditing({ ...editing, descriptionHtml: html })}
                  placeholder="Describe the product — this is exactly how it appears on the product page."
                  testId="prod-description"
                />
              </Field>
              <Field label="IMAGES">
                <div className="flex flex-wrap gap-2 mb-2">
                  {(editing.images || []).map((im, i) => (
                    <div key={i} className="relative h-16 w-16 bg-neutral-100 group">
                      <img src={imgSrc(im)} alt="" className="h-full w-full object-cover" />
                      <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })}
                        className="absolute -top-1.5 -right-1.5 bg-black text-white rounded-full p-0.5" data-testid={`prod-img-remove-${i}`}><X size={12} /></button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <label className="flex items-center gap-2 px-3 h-9 border border-neutral-300 cursor-pointer text-xs font-display tracking-[0.12em] hover:border-[#FF6B00]" data-testid="prod-upload-label">
                    {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} UPLOAD
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="prod-upload-input" />
                  </label>
                  <Input placeholder="…or paste image URL, press Enter" className="rounded-none border-neutral-300 h-9 text-sm"
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value.trim()) { setEditing({ ...editing, images: [...(editing.images || []), e.target.value.trim()] }); e.target.value = ''; } }}
                    data-testid="prod-img-url" />
                </div>
              </Field>
              <Field label="OPTION GROUP NAME (e.g. Material, Finish, Diameter)">
                <Input value={editing.optionName || ''} onChange={(e) => setEditing({ ...editing, optionName: e.target.value })} placeholder="Variant" className="rounded-none border-neutral-300" data-testid="prod-optionname" />
              </Field>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-display tracking-[0.18em] text-neutral-500">VARIANTS / OPTIONS</span>
                  <button onClick={addVariant} className="text-xs text-[#FF6B00] hover:underline flex items-center gap-1" data-testid="prod-add-variant"><Plus size={13} /> Add option</button>
                </div>
                {(editing.variants || []).map((v, i) => (
                  <div key={i} className="border border-neutral-200 p-3 mb-2 bg-neutral-50" data-testid={`prod-variant-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-display tracking-[0.18em] text-neutral-400">OPTION {i + 1}{i === 0 ? ' · BASE' : ''}</span>
                      <button onClick={() => removeVariant(i)} className="text-neutral-400 hover:text-red-600" data-testid={`prod-variant-remove-${i}`}><X size={14} /></button>
                    </div>
                    <Input value={v.option || ''} onChange={(e) => setVariant(i, { option: e.target.value })} placeholder="Option label (e.g. Dry Carbon)" className="rounded-none border-neutral-300 mb-2 h-9 text-sm" data-testid={`prod-variant-option-${i}`} />
                    {i === 0 ? (
                      <p className="text-[10px] text-neutral-400 mb-2">Base option — its price is the reference for the % uplifts below.</p>
                    ) : (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-display tracking-[0.16em] text-neutral-500 whitespace-nowrap">% ABOVE BASE</span>
                        <Input type="number" value={v.upliftPct ?? ''} onChange={(e) => setVariant(i, { upliftPct: e.target.value === '' ? '' : Number(e.target.value) })} placeholder="e.g. 30" className="rounded-none border-neutral-300 h-8 w-24 text-sm" data-testid={`prod-variant-uplift-${i}`} />
                        {hasPct(v.upliftPct) && <span className="text-[10px] text-emerald-600">auto-priced</span>}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <Input type="number" value={v.price ?? ''} onChange={(e) => setVariant(i, { price: Number(e.target.value) })} placeholder="Price" className="rounded-none border-neutral-300 h-9 text-sm" data-testid={`prod-variant-price-${i}`} />
                      <Input type="number" value={v.compareAtPrice ?? ''} onChange={(e) => setVariant(i, { compareAtPrice: e.target.value === '' ? null : Number(e.target.value) })} placeholder="Compare-at" className="rounded-none border-neutral-300 h-9 text-sm" data-testid={`prod-variant-compare-${i}`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" value={v.cost ?? ''} onChange={(e) => setVariant(i, { cost: e.target.value === '' ? null : Number(e.target.value) })} placeholder="Cost / item" className="rounded-none border-neutral-300 h-9 text-sm" data-testid={`prod-variant-cost-${i}`} />
                      <Input value={v.sku || ''} onChange={(e) => setVariant(i, { sku: e.target.value })} placeholder="SKU" className="rounded-none border-neutral-300 h-9 text-sm" data-testid={`prod-variant-sku-${i}`} />
                    </div>
                    <MarginRow price={v.price} cost={v.cost} />
                  </div>
                ))}
                {(!editing.variants || editing.variants.length === 0) && (
                  <p className="text-xs text-neutral-400">No options — this is a single-price product. Add options for finishes, sizes, etc.</p>
                )}
                {(editing.variants || []).length === 1 && (
                  <p className="text-[11px] text-amber-600">Add a 2nd option to show a picker on the product page (a single option acts as base price).</p>
                )}
              </div>
              <div className="border-t border-neutral-200 mt-2 pt-4">
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-neutral-700">Charge tax on this product</span>
                  <Switch checked={editing.chargeTax !== false} onCheckedChange={(v) => setEditing({ ...editing, chargeTax: v })} className="data-[state=checked]:bg-[#FF6B00]" data-testid="prod-charge-tax" />
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-neutral-700">Track inventory quantity</span>
                  <Switch checked={!!editing.trackQuantity} onCheckedChange={(v) => setEditing({ ...editing, trackQuantity: v })} className="data-[state=checked]:bg-[#FF6B00]" data-testid="prod-track-qty" />
                </div>
                {editing.trackQuantity && (
                  <Field label="INVENTORY QUANTITY">
                    <Input type="number" value={editing.inventoryQuantity ?? ''} onChange={(e) => setEditing({ ...editing, inventoryQuantity: e.target.value === '' ? null : Number(e.target.value) })} className="rounded-none border-neutral-300" data-testid="prod-inventory" />
                  </Field>
                )}
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Field label="BARCODE (UPC/GTIN)"><Input value={editing.barcode || ''} onChange={(e) => setEditing({ ...editing, barcode: e.target.value })} className="rounded-none border-neutral-300" data-testid="prod-barcode" /></Field>
                  <Field label="WEIGHT">
                    <div className="flex gap-1">
                      <Input type="number" value={editing.weight ?? ''} onChange={(e) => setEditing({ ...editing, weight: e.target.value === '' ? null : Number(e.target.value) })} className="rounded-none border-neutral-300" data-testid="prod-weight" />
                      <Select value={editing.weightUnit || 'kg'} onValueChange={(v) => setEditing({ ...editing, weightUnit: v })}>
                        <SelectTrigger className="rounded-none border-neutral-300 w-20 text-neutral-900" data-testid="prod-weight-unit"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="kg">kg</SelectItem><SelectItem value="g">g</SelectItem><SelectItem value="lb">lb</SelectItem><SelectItem value="oz">oz</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </Field>
                </div>
                <Field label="PRODUCT DIMENSIONS (package · admin/shipping only)">
                  {(() => {
                    const d = editing.dimensions || { length: '', width: '', height: '', unit: 'cm' };
                    const setD = (k, v) => setEditing({ ...editing, dimensions: { ...d, [k]: v } });
                    return (
                      <div className="flex items-center gap-2">
                        <Input type="number" placeholder="L" value={d.length ?? ''} onChange={(e) => setD('length', e.target.value === '' ? '' : Number(e.target.value))} className="rounded-none border-neutral-300 text-center" data-testid="prod-dim-l" />
                        <span className="text-neutral-400">×</span>
                        <Input type="number" placeholder="W" value={d.width ?? ''} onChange={(e) => setD('width', e.target.value === '' ? '' : Number(e.target.value))} className="rounded-none border-neutral-300 text-center" data-testid="prod-dim-w" />
                        <span className="text-neutral-400">×</span>
                        <Input type="number" placeholder="H" value={d.height ?? ''} onChange={(e) => setD('height', e.target.value === '' ? '' : Number(e.target.value))} className="rounded-none border-neutral-300 text-center" data-testid="prod-dim-h" />
                        <Select value={d.unit || 'cm'} onValueChange={(v) => setD('unit', v)}>
                          <SelectTrigger className="rounded-none border-neutral-300 w-20 text-neutral-900" data-testid="prod-dim-unit"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="cm">cm</SelectItem><SelectItem value="in">in</SelectItem></SelectContent>
                        </Select>
                      </div>
                    );
                  })()}
                </Field>
                <Field label="TAGS (comma-separated)"><Input value={editing.tags || ''} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} placeholder="e.g. Model 3, Front Lip, Carbon" className="rounded-none border-neutral-300" data-testid="prod-tags" /></Field>
                <Field label="SEO PAGE TITLE"><Input value={editing.seoTitle || ''} onChange={(e) => setEditing({ ...editing, seoTitle: e.target.value })} className="rounded-none border-neutral-300" data-testid="prod-seo-title" /></Field>
                <Field label="SEO META DESCRIPTION"><Textarea value={editing.seoDescription || ''} onChange={(e) => setEditing({ ...editing, seoDescription: e.target.value })} rows={2} className="rounded-none border-neutral-300" data-testid="prod-seo-desc" /></Field>
              </div>
              <div className="flex items-center justify-between py-3 border-t border-neutral-200 mt-2">
                <span className="text-sm text-neutral-700">Featured on homepage</span>
                <Switch checked={!!editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} className="data-[state=checked]:bg-[#FF6B00]" data-testid="prod-featured" />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-neutral-200 mb-4">
                <span className="text-sm text-neutral-700">Published (visible on site)</span>
                <Switch checked={editing.published !== false} onCheckedChange={(v) => setEditing({ ...editing, published: v })} className="data-[state=checked]:bg-[#FF6B00]" data-testid="prod-published" />
              </div>
              <Button disabled={saving} onClick={saveProduct} className="w-full h-11 rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.15em]" data-testid="prod-save">
                {saving ? <Loader2 className="animate-spin" size={16} /> : 'SAVE'}
              </Button>
              {!editing._isNew && (
                <>
                  <Button variant="outline" onClick={() => openPreview(editing)}
                    className="w-full h-11 rounded-none border-neutral-300 text-neutral-900 hover:border-[#FF6B00] font-display tracking-[0.15em] text-xs mt-2" data-testid="prod-preview">
                    <ExternalLink className="h-4 w-4 mr-2" /> PREVIEW ON SITE
                  </Button>
                  <Button variant="outline" onClick={() => setProdToDelete(editing)}
                    className="w-full h-11 rounded-none border-red-300 text-red-600 hover:bg-red-50 font-display tracking-[0.15em] text-xs mt-2" data-testid="prod-delete">
                    <Trash2 className="h-4 w-4 mr-2" /> DELETE PRODUCT
                  </Button>
                </>
              )}
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!prodToDelete} onOpenChange={(o) => !o && setProdToDelete(null)}>
        <AlertDialogContent data-testid="prod-delete-dialog" className="bg-white text-neutral-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-900">Delete this product?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-500">
              This permanently removes "{prodToDelete?.name}" from the catalog. This cannot be undone. (To just take it off the site, use the Published toggle instead.)
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-neutral-900 border-neutral-300" data-testid="prod-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { deleteProduct(prodToDelete.id); setProdToDelete(null); }} className="bg-red-600 hover:bg-red-700 text-white" data-testid="prod-delete-confirm">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OrderBuilder
        open={builderOpen}
        onClose={() => { setBuilderOpen(false); setEditingOrder(null); }}
        authFetch={authFetch}
        initialOrder={editingOrder}
        onSaved={onOrderSaved}
      />

      <BulkProductEditor
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        products={products.filter((p) => selectedProducts.includes(p.id))}
        collections={collections}
        authFetch={authFetch}
        onSaved={load}
      />

      <AlertDialog open={importOpen} onOpenChange={(o) => { if (!o) { setImportOpen(false); setImportPreview(null); setImportResult(null); } }}>
        <AlertDialogContent className="bg-white text-neutral-900 max-w-lg" data-testid="import-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-900">{importResult ? 'Import complete' : 'Review import'}</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-500">
              {importResult ? 'Your catalogue has been updated.' : 'Nothing is changed until you confirm. Blank cells leave existing values unchanged.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {importing && !importPreview && <div className="flex items-center gap-2 text-neutral-500 py-6"><Loader2 className="animate-spin" size={16} /> Reading file…</div>}

          {importPreview && !importResult && (
            <div className="space-y-3" data-testid="import-preview">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-neutral-200 p-3"><div className="text-2xl font-display text-green-600" data-testid="import-created">{importPreview.created}</div><div className="text-[11px] tracking-[0.15em] text-neutral-500">CREATE</div></div>
                <div className="border border-neutral-200 p-3"><div className="text-2xl font-display text-blue-600" data-testid="import-updated">{importPreview.updated}</div><div className="text-[11px] tracking-[0.15em] text-neutral-500">UPDATE</div></div>
                <div className="border border-neutral-200 p-3"><div className="text-2xl font-display text-amber-600" data-testid="import-skipped">{importPreview.skipped}</div><div className="text-[11px] tracking-[0.15em] text-neutral-500">SKIP</div></div>
              </div>
              {importPreview.unknown_columns?.length > 0 && (
                <p className="text-xs text-amber-600">Ignored unknown columns: {importPreview.unknown_columns.join(', ')}</p>
              )}
              {importPreview.errors?.length > 0 && (
                <div className="max-h-40 overflow-y-auto border border-amber-200 bg-amber-50 p-2 text-xs space-y-1" data-testid="import-errors">
                  {importPreview.errors.map((e, i) => <div key={i} className="text-amber-800">Row {e.row}: {e.error}</div>)}
                </div>
              )}
              {importPreview.created + importPreview.updated === 0 && <p className="text-sm text-red-600">No valid rows to import.</p>}
            </div>
          )}

          {importResult && (
            <div className="space-y-2 text-sm" data-testid="import-result">
              <div className="text-green-700">{importResult.created} created · {importResult.updated} updated{importResult.failed ? ` · ${importResult.failed} failed` : ''}</div>
              {importResult.fail_details?.length > 0 && (
                <div className="max-h-32 overflow-y-auto border border-red-200 bg-red-50 p-2 text-xs">
                  {importResult.fail_details.map((f, i) => <div key={i} className="text-red-700">{f.slug}: {f.error}</div>)}
                </div>
              )}
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="import-close">{importResult ? 'Done' : 'Cancel'}</AlertDialogCancel>
            {importPreview && !importResult && (
              <AlertDialogAction onClick={(e) => { e.preventDefault(); runImportCommit(); }} disabled={importing || (importPreview.created + importPreview.updated === 0)}
                className="rounded-none bg-black hover:bg-[#FF6B00] font-display tracking-[0.12em] text-xs gap-2" data-testid="import-confirm">
                {importing ? <Loader2 size={14} className="animate-spin" /> : `APPLY (${importPreview.created + importPreview.updated})`}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Admin;
